import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Download,
  Eye,
  Edit,
  Trash2,
  Play,
  Clock,
  FileText,
  Calendar,
  RefreshCw,
} from 'lucide-react';
import { reportService } from '../services/api';
import DataTable from '../components/common/DataTable';

interface Report {
  report_id: string;
  report_name: string;
  report_type: string;
  output_format: string;
  schedule_frequency?: string;
  is_active: boolean;
  last_generated_at?: string;
  created_at: string;
}

const REPORT_TYPES = [
  { value: 'profit_loss', label: 'Profit & Loss' },
  { value: 'balance_sheet', label: 'Balance Sheet' },
  { value: 'cash_flow', label: 'Cash Flow' },
  { value: 'budget_variance', label: 'Budget Variance' },
  { value: 'kpi_summary', label: 'KPI Summary' },
  { value: 'custom', label: 'Custom Report' },
];

const OUTPUT_FORMATS = [
  { value: 'pdf', label: 'PDF', icon: FileText },
  { value: 'excel', label: 'Excel', icon: FileText },
  { value: 'csv', label: 'CSV', icon: FileText },
];

const SCHEDULE_FREQUENCIES = [
  { value: 'none', label: 'No Schedule' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
];

const Reports: React.FC = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newReport, setNewReport] = useState({
    report_name: '',
    report_type: 'profit_loss',
    output_format: 'pdf',
    schedule_frequency: 'none',
  });

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const response = await reportService.getReports();
      setReports(response.data.data || []);
    } catch (err) {
      console.error('Error fetching reports:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReport = async () => {
    try {
      await reportService.createReport({
        ...newReport,
        schedule_frequency: newReport.schedule_frequency === 'none' ? null : newReport.schedule_frequency,
      });
      setShowCreateModal(false);
      setNewReport({
        report_name: '',
        report_type: 'profit_loss',
        output_format: 'pdf',
        schedule_frequency: 'none',
      });
      fetchReports();
    } catch (err) {
      console.error('Error creating report:', err);
    }
  };

  const handleGenerateReport = async (reportId: string) => {
    try {
      await reportService.generateReport(reportId);
      // TODO: Download or display the generated report
      alert('Report generated successfully!');
    } catch (err) {
      console.error('Error generating report:', err);
    }
  };

  const handleDeleteReport = async (reportId: string) => {
    if (window.confirm('Are you sure you want to delete this report?')) {
      try {
        await reportService.deleteReport(reportId);
        fetchReports();
      } catch (err) {
        console.error('Error deleting report:', err);
      }
    }
  };

  const handleToggleActive = async (reportId: string, isActive: boolean) => {
    try {
      await reportService.updateReport(reportId, { is_active: !isActive });
      fetchReports();
    } catch (err) {
      console.error('Error updating report:', err);
    }
  };

  const reportColumns = [
    {
      key: 'report_name',
      label: 'Report Name',
      sortable: true,
      format: (value: string, row: Report) => (
        <div>
          <div className="font-medium text-gray-900">{value}</div>
          <div className="text-xs text-gray-500">
            {REPORT_TYPES.find((t) => t.value === row.report_type)?.label || row.report_type}
          </div>
        </div>
      ),
    },
    {
      key: 'output_format',
      label: 'Format',
      format: (value: string) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 uppercase">
          {value}
        </span>
      ),
      align: 'center' as const,
    },
    {
      key: 'schedule_frequency',
      label: 'Schedule',
      format: (value: string) => (
        <div className="flex items-center space-x-1">
          <Clock className="w-4 h-4 text-gray-400" />
          <span className="text-sm text-gray-700">
            {value ? SCHEDULE_FREQUENCIES.find((f) => f.value === value)?.label : 'Manual'}
          </span>
        </div>
      ),
    },
    {
      key: 'last_generated_at',
      label: 'Last Generated',
      sortable: true,
      format: (value: string) =>
        value ? (
          <span className="text-sm text-gray-700">
            {new Date(value).toLocaleDateString()}
          </span>
        ) : (
          <span className="text-sm text-gray-400">Never</span>
        ),
    },
    {
      key: 'is_active',
      label: 'Status',
      format: (value: boolean, row: Report) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleToggleActive(row.report_id, value);
          }}
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer ${
            value
              ? 'bg-green-100 text-green-800 hover:bg-green-200'
              : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
          }`}
        >
          {value ? 'Active' : 'Inactive'}
        </button>
      ),
      align: 'center' as const,
    },
    {
      key: 'actions',
      label: 'Actions',
      align: 'center' as const,
      format: (_: any, row: Report) => (
        <div className="flex items-center justify-center space-x-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleGenerateReport(row.report_id);
            }}
            className="p-1 text-blue-600 hover:text-blue-800"
            title="Generate Now"
          >
            <Play className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/reports/${row.report_id}`);
            }}
            className="p-1 text-gray-600 hover:text-gray-800"
            title="View"
          >
            <Eye className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/reports/${row.report_id}/edit`);
            }}
            className="p-1 text-green-600 hover:text-green-800"
            title="Edit"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteReport(row.report_id);
            }}
            className="p-1 text-red-600 hover:text-red-800"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Reports</h1>
          <p className="mt-1 text-sm text-gray-500">
            Create, schedule, and manage financial reports
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={fetchReports}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5 mr-2" />
            New Report
          </button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Reports</p>
              <p className="mt-2 text-3xl font-bold text-gray-900">{reports.length}</p>
            </div>
            <FileText className="w-8 h-8 text-blue-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Active Schedules</p>
              <p className="mt-2 text-3xl font-bold text-gray-900">
                {reports.filter((r) => r.schedule_frequency && r.is_active).length}
              </p>
            </div>
            <Calendar className="w-8 h-8 text-green-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Generated Today</p>
              <p className="mt-2 text-3xl font-bold text-gray-900">0</p>
            </div>
            <Download className="w-8 h-8 text-purple-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Inactive</p>
              <p className="mt-2 text-3xl font-bold text-gray-900">
                {reports.filter((r) => !r.is_active).length}
              </p>
            </div>
            <Clock className="w-8 h-8 text-gray-400" />
          </div>
        </div>
      </div>

      {/* Reports List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">All Reports</h2>
        </div>
        <DataTable
          columns={reportColumns}
          data={reports}
          loading={loading}
          emptyMessage="No reports found. Create your first report to get started."
          onRowClick={(row) => navigate(`/reports/${row.report_id}`)}
        />
      </div>

      {/* Create Report Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Create New Report</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Report Name
                </label>
                <input
                  type="text"
                  value={newReport.report_name}
                  onChange={(e) =>
                    setNewReport({ ...newReport, report_name: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Monthly P&L Report"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Report Type
                </label>
                <select
                  value={newReport.report_type}
                  onChange={(e) =>
                    setNewReport({ ...newReport, report_type: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {REPORT_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Output Format
                </label>
                <select
                  value={newReport.output_format}
                  onChange={(e) =>
                    setNewReport({ ...newReport, output_format: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {OUTPUT_FORMATS.map((format) => (
                    <option key={format.value} value={format.value}>
                      {format.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Schedule Frequency
                </label>
                <select
                  value={newReport.schedule_frequency}
                  onChange={(e) =>
                    setNewReport({ ...newReport, schedule_frequency: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {SCHEDULE_FREQUENCIES.map((freq) => (
                    <option key={freq.value} value={freq.value}>
                      {freq.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end space-x-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateReport}
                disabled={!newReport.report_name}
                className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Create Report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;
